/**
 * Storage settings page — detailed view of the billing-honest storage number.
 *
 * Layout (locked 2026-07-21):
 *   1. Peak-vs-cap card — the BIG number. peakLast30dBytes / 1 TB soft cap.
 *      Bar tints red when over. Overuse section renders below when over.
 *   2. Current-vs-cap card — the immediate number. totalBytes / 1 TB.
 *   3. Breakdown card — Active vs Trash split. Both count toward the cap
 *      (spec: [[project-trash-delete-model]]).
 *   4. 30-day trend — inline SVG sparkline sourced from /storage/history.
 *
 * Soft cap = 1 TB hardcoded. Phase 2 replaces with users.storage_cap_bytes.
 * No upload gate today — this page is display-only.
 *
 * Data comes from three endpoints that already exist (Task 6, 2026-07-05):
 *   GET /api/admin/storage/current
 *   GET /api/admin/storage/peak
 *   GET /api/admin/storage/history
 */

import { HardDrive, TrendingUp, Trash2, AlertTriangle } from "lucide-react";
import { SettingsPageHeader } from "@/pages/settings/settings-placeholder";
import { useAuth } from "@/hooks/use-auth";
import {
  useStorageCurrent,
  useStorageHistory,
  useStoragePeak,
  type StorageHistoryRow,
} from "@/hooks/use-storage";

// 1 TB soft cap literal — Phase 2 replaces with users.storage_cap_bytes.
const SOFT_CAP_BYTES = 1024 ** 4;

function formatBytes(n: number | undefined | null): string {
  if (typeof n !== "number" || !Number.isFinite(n) || n < 0) return "—";
  if (n === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v < 10 ? v.toFixed(2) : v < 100 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

function formatDate(value: string | undefined | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function StorageSettingsPage() {
  const { role } = useAuth();
  const isAdmin = role === "ADMIN";

  const current = useStorageCurrent(isAdmin);
  const peak = useStoragePeak(isAdmin);
  const history = useStorageHistory(isAdmin);

  const peakBytes = peak.data?.peakLast30dBytes ?? 0;
  const currentBytes = current.data?.totalBytes ?? 0;
  const overageBytes = Math.max(0, peakBytes - SOFT_CAP_BYTES);
  const isOver = overageBytes > 0;

  return (
    <div>
      <SettingsPageHeader
        title="Storage"
        subtitle="Your workspace's storage against the 1 TB soft cap."
      />

      {/* Peak — the billing metric. This is the one that matters for money. */}
      <div className="rounded-2xl border border-border bg-secondary/40 p-6">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-secondary text-muted-foreground">
            <TrendingUp className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-sm font-medium text-foreground">
              Peak last 30 days · billed on this
            </div>
            <div className="text-xs text-muted-foreground">
              {peak.data?.peakDate
                ? `Highest was ${formatDate(peak.data.peakDate)}`
                : "Rolling 30 days"}
            </div>
          </div>
        </div>

        {peak.isLoading ? (
          <div className="mt-6 h-6 w-40 animate-pulse rounded bg-secondary" />
        ) : peak.isError ? (
          <div className="mt-6 text-sm text-destructive">Couldn't load peak.</div>
        ) : (
          <UsageBar value={peakBytes} highlight={isOver} />
        )}

        {/* Overuse alert — locked 2026-07-21: peak drives the alert, not current. */}
        {isOver && !peak.isLoading && (
          <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" strokeWidth={2} />
              <div className="text-sm">
                <div className="font-medium text-destructive">
                  Overuse: {formatBytes(overageBytes)} above the 1 TB cap
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Storage is a soft cap — you can keep uploading, but the overage
                  is billed. Cap enforcement lands in Phase 2.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Current — immediate truth, second-tier. */}
      <div className="mt-4 rounded-2xl border border-border bg-secondary/40 p-6">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-secondary text-muted-foreground">
            <HardDrive className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-sm font-medium text-foreground">Current usage</div>
            <div className="text-xs text-muted-foreground">
              Right now — active files + trash. Both count.
            </div>
          </div>
        </div>

        {current.isLoading ? (
          <div className="mt-6 h-6 w-40 animate-pulse rounded bg-secondary" />
        ) : current.isError ? (
          <div className="mt-6 text-sm text-destructive">Couldn't load current usage.</div>
        ) : (
          <>
            <UsageBar value={currentBytes} highlight={currentBytes > SOFT_CAP_BYTES} />
            <div className="mt-4 grid grid-cols-2 gap-3">
              <BreakdownTile
                icon={<HardDrive className="h-4 w-4" strokeWidth={2} />}
                label="Active"
                bytes={current.data?.activeBytes ?? 0}
                tone="foreground"
              />
              <BreakdownTile
                icon={<Trash2 className="h-4 w-4" strokeWidth={2} />}
                label="Trash"
                bytes={current.data?.trashBytes ?? 0}
                tone="muted"
              />
            </div>
          </>
        )}
      </div>

      {/* 30-day sparkline */}
      <div className="mt-4 rounded-2xl border border-border bg-secondary/40 p-6">
        <div className="mb-3">
          <div className="text-sm font-medium text-foreground">30-day trend</div>
          <div className="text-xs text-muted-foreground">
            Total daily storage (active + trash) over the rolling 30-day window.
          </div>
        </div>
        {history.isLoading ? (
          <div className="h-24 animate-pulse rounded-lg bg-secondary" />
        ) : history.isError ? (
          <div className="text-sm text-destructive">Couldn't load history.</div>
        ) : (
          <Sparkline data={history.data ?? []} capBytes={SOFT_CAP_BYTES} />
        )}
      </div>
    </div>
  );
}

// ── Usage bar ────────────────────────────────────────────────────────────────

function UsageBar({ value, highlight }: { value: number; highlight: boolean }) {
  const fraction = Math.min(1, value / SOFT_CAP_BYTES);
  return (
    <>
      <div className="mt-6 flex items-baseline gap-1">
        <span className="text-3xl font-semibold tabular-nums text-foreground">
          {formatBytes(value)}
        </span>
        <span className="ml-1 text-sm text-muted-foreground">of 1 TB</span>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary">
        <div
          className={
            "h-full rounded-full transition-all " +
            (highlight ? "bg-destructive" : "bg-primary")
          }
          style={{ width: `${Math.max(2, fraction * 100)}%` }}
        />
      </div>
    </>
  );
}

// ── Breakdown tile (Active / Trash) ──────────────────────────────────────────

function BreakdownTile({
  icon,
  label,
  bytes,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  bytes: number;
  tone: "foreground" | "muted";
}) {
  return (
    <div className="rounded-xl border border-border bg-background/60 px-4 py-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div
        className={
          "mt-1 text-lg font-semibold tabular-nums " +
          (tone === "foreground" ? "text-foreground" : "text-muted-foreground")
        }
      >
        {formatBytes(bytes)}
      </div>
    </div>
  );
}

// ── Sparkline (inline SVG, no dep) ───────────────────────────────────────────

function Sparkline({
  data,
  capBytes,
}: {
  data: StorageHistoryRow[];
  capBytes: number;
}) {
  if (data.length === 0) {
    return (
      <div className="grid h-24 place-items-center text-xs text-muted-foreground">
        No snapshots yet — check back tomorrow.
      </div>
    );
  }

  // Fixed viewBox — polyline scales into the container. Height 60 gives room
  // for the axis line + a tiny margin above the peak.
  const W = 600;
  const H = 60;
  const PAD_X = 4;
  const PAD_Y = 6;

  // Y scale — cap the axis at the larger of (max value in window) OR
  // the soft cap so the bar and the line share a scale visually.
  const maxObserved = Math.max(...data.map((d) => d.totalBytes || 0), 0);
  const yMax = Math.max(maxObserved, capBytes * 0.1); // keep some headroom if empty

  const xStep = data.length > 1 ? (W - PAD_X * 2) / (data.length - 1) : 0;
  const points = data
    .map((d, i) => {
      const x = PAD_X + i * xStep;
      const y = H - PAD_Y - ((d.totalBytes || 0) / yMax) * (H - PAD_Y * 2);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  // Cap line — only render if the cap is within the visible y-range.
  const capY = H - PAD_Y - (capBytes / yMax) * (H - PAD_Y * 2);
  const capVisible = capY >= PAD_Y && capY <= H - PAD_Y;

  const first = data[0];
  const last = data[data.length - 1];

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-24 w-full"
        preserveAspectRatio="none"
        aria-label="30-day storage trend"
      >
        {/* Cap reference line — only when in range */}
        {capVisible && (
          <line
            x1={PAD_X}
            x2={W - PAD_X}
            y1={capY}
            y2={capY}
            stroke="currentColor"
            strokeDasharray="3 3"
            strokeWidth={1}
            className="text-destructive/50"
          />
        )}
        {/* Filled area under the line */}
        <polyline
          points={`${PAD_X},${H - PAD_Y} ${points} ${W - PAD_X},${H - PAD_Y}`}
          fill="currentColor"
          className="text-primary/15"
        />
        {/* The line itself */}
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          className="text-primary"
        />
      </svg>
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>{formatDate(first?.date)}</span>
        <span className="tabular-nums">
          {formatBytes(last?.totalBytes ?? 0)} today
        </span>
        <span>{formatDate(last?.date)}</span>
      </div>
    </div>
  );
}
